import React, { useRef } from 'react';

let _counter = 0;

/**
 * Liquid "split & merge" loader — fully CSS-animated (no SMIL).
 *
 * Two large drops sit at the SVG centre, swing apart in opposite directions
 * (antiphase), then swing back and merge into one mass every 3 s.
 * Three satellites orbit at staggered radii/speeds, pinching tendrils off
 * the main mass as they pass close.
 *
 * Colors derive from --accent-light / --accent-hover, so they react instantly
 * to Settings changes without any React re-render.
 */
export default function ApibooLoader({ size = 220 }) {
  const id  = useRef(++_counter).current;
  const fid = `goo-${id}`;
  const gid = `g-${id}`;

  /* ── CSS animations injected as a <style> block inside the SVG ──────────
     All class names are instance-scoped (suffix = id) so multiple loaders
     on the same page never collide.
  ────────────────────────────────────────────────────────────────────────── */
  const css = `
    /* Blob A: starts at centre, swings LEFT at 25 %, back at 50 %, RIGHT at 75 % */
    @keyframes ba${id} {
      0%,50%,100% { transform: translate(   0px,  0px) scale(1.07); }
      25%          { transform: translate( -38px,  6px) scale(0.88); }
      75%          { transform: translate(  38px, -6px) scale(0.88); }
    }
    /* Blob B: exact antiphase of A */
    @keyframes bb${id} {
      0%,50%,100% { transform: translate(   0px,  0px) scale(1.05); }
      25%          { transform: translate(  38px, -6px) scale(0.86); }
      75%          { transform: translate( -38px,  6px) scale(0.86); }
    }
    /* Shared orbital keyframes — apply to <g> wrappers whose transform-origin
       is fixed at the SVG centre (110 110).  The child circle sits at its orbit
       radius along the X axis; parent rotation sweeps it around the centre. */
    @keyframes cw${id}  { to { transform: rotate( 360deg); } }
    @keyframes ccw${id} { to { transform: rotate(-360deg); } }

    .ba${id} {
      animation: ba${id} 6s cubic-bezier(.42,0,.58,1) infinite;
    }
    .bb${id} {
      animation: bb${id} 6s cubic-bezier(.42,0,.58,1) infinite;
    }
    /* Satellite wrappers — transform-origin locked to SVG centre */
    .s1${id} {
      transform-origin: 110px 110px;
      animation: cw${id}  4.4s linear          infinite;
    }
    .s2${id} {
      transform-origin: 110px 110px;
      animation: ccw${id} 6.8s linear  -2.3s   infinite;
    }
    .s3${id} {
      transform-origin: 110px 110px;
      animation: cw${id}  3.1s linear  -1.05s  infinite;
    }
  `;

  return (
    <div
      className="apiboo-loader"
      style={{ width: size, height: size, display: 'inline-block', flexShrink: 0 }}
    >
      <svg
        viewBox="0 0 220 220"
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        aria-hidden="true"
      >
        <defs>
          <style>{css}</style>

          {/*
            Goo filter — blur then threshold the alpha channel.
            Overlapping blobs add their blurred halos; wherever the sum
            crosses the threshold the filter renders them as one merged mass.
          */}
          <filter
            id={fid}
            x="-35%" y="-35%"
            width="170%" height="170%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="9" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 20 -8"
            />
          </filter>

          {/*
            objectBoundingBox gradient — each circle gets its own copy scaled
            to its own bounding box, so the highlight travels with the blob.
            stop-color via CSS custom properties → live accent-colour updates.
          */}
          <radialGradient id={gid} cx="38%" cy="32%" r="68%">
            <stop offset="0%"   style={{ stopColor: 'var(--accent-light)' }} />
            <stop offset="100%" style={{ stopColor: 'var(--accent-hover)' }} />
          </radialGradient>
        </defs>

        <g filter={`url(#${fid})`}>

          {/* ── Main blobs ────────────────────────────────────────────────── */}
          {/* Both start at SVG centre (110,110); CSS translate moves them apart */}
          <circle
            cx="110" cy="110" r="33"
            fill={`url(#${gid})`}
            className={`ba${id}`}
          />
          <circle
            cx="110" cy="110" r="31"
            fill={`url(#${gid})`}
            className={`bb${id}`}
          />

          {/* ── Satellites ────────────────────────────────────────────────── */}
          {/*
            Each satellite lives inside a <g> whose transform-origin is the SVG
            centre.  The child circle is placed at (centre + orbit-radius, centre)
            so rotating the <g> sweeps it in a perfect circle.

            Orbit radii chosen so the satellite just skims the main mass at its
            closest point → goo filter stretches a visible tendril.

            Bounds check (cx + r ≤ 220 − margin):
              S1: 110+40+11 = 161  ✓
              S2: 110+48+9  = 167  ✓
              S3: 110+28+7  = 145  ✓
          */}

          {/* Satellite 1 — orbit r=40, clockwise 4.4 s */}
          <g className={`s1${id}`}>
            <circle cx="150" cy="110" r="11" fill={`url(#${gid})`} />
          </g>

          {/* Satellite 2 — orbit r=48, counter-clockwise 6.8 s, offset phase */}
          <g className={`s2${id}`}>
            <circle cx="158" cy="110" r="9" fill={`url(#${gid})`} />
          </g>

          {/* Satellite 3 — orbit r=28, clockwise 3.1 s, different offset */}
          <g className={`s3${id}`}>
            <circle cx="138" cy="110" r="7" fill={`url(#${gid})`} />
          </g>

        </g>
      </svg>
    </div>
  );
}
